import React, { useState, useEffect } from 'react';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Drawer from '@mui/material/Drawer';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import MenuIcon from '@mui/icons-material/Menu';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import useScrollTrigger from '@mui/material/useScrollTrigger';
import BigBoardLogo, { pngBoxStyle } from './logo/BigBoardLogo';

function ElevationScroll(props) {
  const { children } = props;
  const trigger = useScrollTrigger({
    disableHysteresis: true,
    threshold: 0,
  });

  return React.cloneElement(children, {
    elevation: trigger ? 4 : 0,
  });
}

const styles = {
  toolbarMargin: {
    marginBottom: '7em',
  },
  logo: {
    height: '7em',
  },
  logoContainer: {
    padding: 0,
    '&:hover': {
      backgroundColor: 'transparent',
    },
  },
  background: {
    background: 'black',
  },
  tabContainer: {
    marginLeft: 'auto',
  },
  tab: {
    fontFamily: 'Arial',
    textTransform: 'none',
    fontWeight: 700,
    fontSize: '1rem',
    color: 'White',
    minWidth: 10,
    marginLeft: '25px',
  },
};


export default function Header(props) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [value, setValue] = useState(0);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [accountMenuAnchor, setAccountMenuAnchor] = useState(null);
  const { logout, currentUser, isAdmin, isApproved } = useAuth();
  // Pool navigation is for approved members; a pending account gets only
  // the brand link and Log out.
  const member = !!currentUser && isApproved;
  const navigate = useNavigate();


  const handleChange = (e, value) => {
    setValue(value);
  };

  const handleLogout = async (e) => {
    try {
      await logout();
    } catch {
      // handle error
    }
  };

  useEffect(() => {
    if (!currentUser) {
      setValue(false);
      return;
    }
    const path = window.location.pathname;
    if (path === '/') setValue(0);
    else if (path === '/leaderboard') setValue(1);
    else if (path === '/faq') setValue(2);
    else if (path.startsWith('/admin') && isAdmin) setValue(3);
    else if (path === '/profile') setValue(false);
    else setValue(false);
  }, [value, currentUser, isAdmin]);

  return (
    <React.Fragment>
      <ElevationScroll>
        <AppBar position="fixed" style={styles.background}>
          <Toolbar>
            <Button
              component={Link}
              to="/"
              onClick={() => setValue(currentUser ? 0 : false)}
              style={styles.logoContainer}
              disableRipple
            >
              <BigBoardLogo
                data-logo-target="header"
                style={{ ...pngBoxStyle(isMobile ? '4em' : '7em'), marginLeft: isMobile ? '2px' : 0 }}
              />
            </Button>
            {isMobile ? (
              <>
                <IconButton
                  color="inherit"
                  edge="end"
                  onClick={() => setDrawerOpen(true)}
                  sx={{ marginLeft: 'auto', marginRight: '8px' }}
                >
                  <MenuIcon />
                </IconButton>
                <Drawer
                  anchor="right"
                  open={drawerOpen}
                  onClose={() => setDrawerOpen(false)}
                >
                  <List sx={{ width: 250, pt: 2 }}>
                    {member && (
                      <ListItem disablePadding>
                        <ListItemButton component={Link} to="/" onClick={() => { setValue(0); setDrawerOpen(false); }}>
                          <ListItemText primary="My Picks" />
                        </ListItemButton>
                      </ListItem>
                    )}
                    {member && (
                      <ListItem disablePadding>
                        <ListItemButton component={Link} to="/leaderboard" onClick={() => { setValue(1); setDrawerOpen(false); }}>
                          <ListItemText primary="Leaderboard" />
                        </ListItemButton>
                      </ListItem>
                    )}
                    {member && (
                      <ListItem disablePadding>
                        <ListItemButton component={Link} to="/faq" onClick={() => { setValue(2); setDrawerOpen(false); }}>
                          <ListItemText primary="FAQ" />
                        </ListItemButton>
                      </ListItem>
                    )}
                    {member && (
                      <ListItem disablePadding>
                        <ListItemButton component={Link} to="/profile" onClick={() => { setValue(false); setDrawerOpen(false); }}>
                          <ListItemText primary="Profile" />
                        </ListItemButton>
                      </ListItem>
                    )}
                    {isAdmin && (
                      <ListItem disablePadding>
                        <ListItemButton component={Link} to="/admin/games" onClick={() => { setValue(3); setDrawerOpen(false); }}>
                          <ListItemText primary="Admin" />
                        </ListItemButton>
                      </ListItem>
                    )}
                    <ListItem disablePadding>
                      <ListItemButton
                        component={Link}
                        to="/login"
                        onClick={() => { if (currentUser) handleLogout(); setDrawerOpen(false); }}
                      >
                        <ListItemText primary={currentUser ? "Logout" : "Log In"} />
                      </ListItemButton>
                    </ListItem>
                    {!currentUser && (
                      <ListItem disablePadding>
                        <ListItemButton component={Link} to="/signup" onClick={() => setDrawerOpen(false)}>
                          <ListItemText primary="Sign Up" />
                        </ListItemButton>
                      </ListItem>
                    )}
                  </List>
                </Drawer>
              </>
            ) : (
              <>
                <Tabs value={value} onChange={handleChange} style={styles.tabContainer}>
                  {member && <Tab style={styles.tab} component={Link} to="/" label="My Picks" />}
                  {member && <Tab style={styles.tab} component={Link} to="/leaderboard" label="Leaderboard" />}
                  {member && <Tab style={styles.tab} component={Link} to="/faq" label="FAQ" />}
                  {isAdmin && <Tab style={styles.tab} component={Link} to="/admin/games" label="Admin" />}
                  {!currentUser && <Tab style={styles.tab} component={Link} to="/login" label="Log In" />}
                  {!currentUser && <Tab style={styles.tab} component={Link} to="/signup" label="Sign Up" />}
                </Tabs>
                {currentUser && (
                  <>
                    <IconButton
                      color="inherit"
                      onClick={(e) => setAccountMenuAnchor(e.currentTarget)}
                      data-testid="account-icon"
                      sx={{ ml: 2 }}
                    >
                      <AccountCircleIcon />
                    </IconButton>
                    <Menu
                      anchorEl={accountMenuAnchor}
                      open={Boolean(accountMenuAnchor)}
                      onClose={() => setAccountMenuAnchor(null)}
                    >
                      {member && (
                        <MenuItem onClick={() => { setAccountMenuAnchor(null); setValue(false); navigate('/profile'); }}>
                          Profile
                        </MenuItem>
                      )}
                      <MenuItem onClick={() => { setAccountMenuAnchor(null); handleLogout(); }}>
                        Logout
                      </MenuItem>
                    </Menu>
                  </>
                )}
              </>
            )}
          </Toolbar>
        </AppBar>
      </ElevationScroll>
      <div style={{ marginBottom: isMobile ? '4em' : '7em' }} />
    </React.Fragment>
  );
}
